
  import { ApiError, NETWORK_ERROR } from '@/services/api';
  import prismaticClient from '@/services/integrations/prismaticClient';
  import { Log, LogSeverityLevel } from '@/services/integrations/types';
  import { useIntegrationDetailsStore } from '@/stores/integrationDetails';
  import { user } from '@/ui/auth/auth';
  import { Box } from '@/ui/components/Box';
  import { Button } from '@/ui/components/Button';
  import { InputField } from '@/ui/components/form';
  import { Loading } from '@/ui/components/Loading';
  import { ApiErrorMessage } from '@/ui/components/message/ApiErrorMessage';
  import { Pagination } from '@/ui/components/Pagination';
  import { ResourceTable } from '@/ui/components/table/ResourceTable';
  import { usePagination } from '@/ui/concerns';
  import { computed, defineComponent, ref, watch } from 'vue';

  export default defineComponent({
    components: {
      InputField,
      Box,
      ResourceTable,
      Loading,
      ApiErrorMessage,
      Button,
      Pagination,
    },
    setup() {
      const store = useIntegrationDetailsStore();
      const integration = store.getAndCheckIntegrationActivated();

      const logs = ref<Log[]>([]);
      const loading = ref(true);
      const apiError = ref<ApiError|null>(null);
      const searchQuery = ref('');
      const searchIndex = ref([] as string[]);
      const total = computed(() => filteredLogs.value.length);

      const { page, perPage, resetPagination } = usePagination();

      const startDate = (() => {
        const date = new Date();
        date.setDate(date.getDate() - 1);
        return date;
      })();
      const endDate = new Date;

      watch(searchQuery, () => {
        resetPagination();
      });

      const filteredLogs = computed(() => {
        if (searchQuery.value === '') {
          return logs.value;
        }

        const query = searchQuery.value.toLowerCase();
        return logs.value.filter((log, index) => {
          return searchIndex.value[index].includes(query);
        });
      });

      const paginatedLogs = computed(() => {
        const start = (page.value - 1) * perPage.value;
        const end = page.value * perPage.value;
        return filteredLogs.value.slice(start, end);
      });

      const loadLogs = () => {
        loading.value = true;
        apiError.value = null;

        prismaticClient.getLogs(
          integration.instances.nodes[0].id,
          startDate,
          endDate,
        ).then(result => {
          logs.value = result.nodes;
          loading.value = false;
          buildSearchIndex();
        }).catch(e => {
          apiError.value = new ApiError(NETWORK_ERROR, e.message);
          loading.value = false;
        });
      };

      const buildSearchIndex = () => {
        searchIndex.value = logs.value.map(log => {
          return `${log.severity} ${log.timestamp} ${log.message}`.toLowerCase();
        });
      };

      const formatDate = (date: string) => {
        const d = new Date(date);
        return d.toLocaleString(user.value.locale);
      };

      const getSeverityColor = (severity: LogSeverityLevel) => {
        switch (severity) {
          case 'INFO':
          case 'METRIC':
            return '#00c389';
          case 'WARN':
            return '#ffdd57';
          case 'ERROR':
          case 'FATAL':
            return '#ff3860';
          case 'DEBUG':
          case 'TRACE':
          default:
            return '#808080';
        }
      };

      const setSearchQuery = (query: string) => {
        searchQuery.value = query;
      };

      loadLogs();

      return {
        paginatedLogs,
        loading,
        apiError,
        loadLogs,
        formatDate,
        getSeverityColor,
        searchQuery,
        setSearchQuery,
        page,
        perPage,
        total,
      };
    },
  });
